import React, { createContext, PropsWithChildren, useContext, useMemo } from 'react'

import { CampaignItem, ChatbotCampaignItem, MultimessageItem, SMSBlastCampaignItem } from '@/app/module/campaigns/types'

type ItemValue = {
  item?: CampaignItem
  setItem?:
    | ((item: SMSBlastCampaignItem) => void)
    | ((item: MultimessageItem) => void)
    | ((item: ChatbotCampaignItem) => void)
}

export type CampaignActionContext = {
  hasQuotas: boolean
}

const defaultValue = {}

const ItemContext = createContext<ItemValue>(defaultValue)

type Props = PropsWithChildren<{
  item: CampaignItem
  setItem:
    | ((item: SMSBlastCampaignItem) => void)
    | ((item: MultimessageItem) => void)
    | ((item: ChatbotCampaignItem) => void)
}>

const ItemProvider: React.FC<Props> = ({ children, item, setItem }) => {
  const value = useMemo(() => ({ item, setItem }), [item, setItem])

  return <ItemContext.Provider value={value}>{children}</ItemContext.Provider>
}

export const useItemContext = () => {
  const context = useContext(ItemContext)

  if (!context) {
    throw new Error('useItemContext must be used within an ItemProvider')
  }

  return context
}

export const useCampaignActionContext = (): CampaignActionContext => {
  const { item } = useItemContext()
  const hasQuotas = Boolean(item && 'quotas' in item && item.quotas?.length)

  return useMemo(
    () => ({
      hasQuotas,
    }),
    [hasQuotas],
  )
}

export default ItemProvider
